import React, { useState } from 'react';
import { Button, TextField, Modal, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AWS from 'aws-sdk';
import { awsExports } from '../../aws-exports';
import { firstNameState, displayRoleState, selectedTeamState } from "../../store/atoms";
import { useRecoilState } from 'recoil';

const RenameFilePopup = ({ open, onClose, onConfirm, initialFileName, matchedFields, data, importedHeaders, requiredHeaders2, uploadType }) => {
  const [fileName, setFileName] = useState(initialFileName);
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useRecoilState(selectedTeamState);

  const formatDate = (dateValue) => {
    if (!dateValue) return ''; // Return empty string if the date is null or undefined
  
    // Check if the dateValue is a serial number (Excel date)
    if (typeof dateValue === 'number') {
      const date = new Date((dateValue - 25569) * 86400 * 1000); // Convert Excel serial number to JS date
      return date.toLocaleDateString('en-US'); // Format as MM/DD/YYYY
    }
  
    // If the dateValue is already a string, return it as is
    return dateValue;
  };

  const handleConfirm = async () => {
    try {
      // Restructure the data
      const restructuredData = [requiredHeaders2];
      const headerIndexMap = importedHeaders.reduce((acc, header, index) => {
        acc[header] = index;
        return acc;
      }, {});

      data.forEach(row => {
        const newRow = requiredHeaders2.map(requiredHeader => {
          const matchedField = matchedFields[requiredHeader];
          if (matchedField && headerIndexMap.hasOwnProperty(matchedField)) {
            const value = row[headerIndexMap[matchedField]];
            // Format the value if it's a date field
            if (requiredHeader === 'Date Received') {
              return formatDate(value);
            }
            return value;
          } else {
            return '';
          }
        });
        restructuredData.push(newRow);
      });

      // Create worksheet from restructured data
      const ws = XLSX.utils.aoa_to_sheet(restructuredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const csvContent = XLSX.utils.sheet_to_csv(ws);

      // Upload to S3
      const s3 = new AWS.S3({
        accessKeyId: awsExports.S3.accessKeyId,
        secretAccessKey: awsExports.S3.secretAccessKey,
        region: awsExports.S3.region,
      });

      const formattedDateTime = getFormattedDateTimeUTC();
      const tenantGroup = selectedTeam.name;
      const folderPath = `Tenants/${tenantGroup}/ClientCreate/`;
      const fileExtension = ".csv";
      const newFilename = `${fileName.replace(/[^a-zA-Z0-9]/g, '_')}_${formattedDateTime}${fileExtension}`;

      const uploadParams = {
        Bucket: awsExports.S3.bucket,
        Key: `${folderPath}${newFilename}`,
        Body: csvContent,
        ContentType: 'text/csv',
      };

      await s3.upload(uploadParams).promise();
      // console.log(`File uploaded successfully to ${folderPath}${newFilename}`);

      // Navigate back to the import page with success state
      navigate('/import', {
        state: {
          success: true,
          message: `Import file to create column in database successfully completed`,
          uploadedFile: {
            name: fileName,
            type: uploadType,
            dateUploaded: new Date().toLocaleDateString(),
            progress: 100,
            total: `${(new Blob([csvContent]).size / (1024 * 1024)).toFixed(2)} MB`,
            email: '-',
            mobile: '-',
          },
          isCustomDesign: true,
          isETLLoading: true
        },
      });

      onClose();
    } catch (error) {
      console.error('Error uploading to S3:', error);
      alert('An error occurred while uploading the file. Please try again.');
    }
  };

  const getFormattedDateTimeUTC = () => {
    const now = new Date();
    return now.toISOString().replace(/[:\-]|\.\d{3}/g, '');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.10)',
          p: '20px',
          borderRadius: '14px',
          border: '1px solid #F2F2F2',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 11V17"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 14H15"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography variant="h6" component="h2">
              Rename File
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Divider Line */}
        <Box sx={{ borderBottom: '1px solid #D9D9D9', width: '100%' }} />

        {/* Text Field Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            placeholder="Enter folder name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '7px',
              },
            }}
          />
        </Box>

        {/* Divider Line */}
        <Box sx={{ borderTop: '1px solid #D9D9D9', width: '100%' }} />

        {/* Create Button Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
            <Button
                onClick={handleConfirm}
                sx={{
                    display: 'flex',
                    padding: '10px 14px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '7px',
                    backgroundColor: '#EBAE12',
                    color: '#FFF',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '120%',
                    textTransform: 'none',
                    '&:hover': {
                    backgroundColor: '#EBAE12',
                    },
                }}
                >
                CREATE
            </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RenameFilePopup;